import React, { useState, useEffect, useRef } from "react";
import MarketingLayout from "../components/MarketingLayout";
import { PLANS, BILLING_NOTE } from "../data/plans";
import { FAQS } from "../data/faqs";
import { BRAND } from "../data/brand";

const inr = n => "₹" + n.toLocaleString("en-IN");

// ─── Animated counter ─────────────────────────────────────────────────────────
function Counter({ to, suffix = "", prefix = "" }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      obs.disconnect();
      let start = null;
      const step = ts => {
        if (!start) start = ts;
        const p = Math.min((ts - start) / 1200, 1);
        setVal(Math.floor(p * to));
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }, { threshold: 0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [to]);
  return <span ref={ref}>{prefix}{val.toLocaleString("en-IN")}{suffix}</span>;
}

// ─── Blueprint grid SVG bg ────────────────────────────────────────────────────
function BlueprintBg({ opacity = 0.04 }) {
  return (
    <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }} xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="bp-sm" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M40 0L0 0 0 40" fill="none" stroke="#60a5fa" strokeWidth="0.4"/>
        </pattern>
        <pattern id="bp-lg" width="200" height="200" patternUnits="userSpaceOnUse">
          <rect width="200" height="200" fill="none" stroke="#60a5fa" strokeWidth="0.8"/>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#bp-sm)`} opacity={opacity}/>
      <rect width="100%" height="100%" fill={`url(#bp-lg)`} opacity={opacity * 1.5}/>
    </svg>
  );
}

// ─── Feature card ─────────────────────────────────────────────────────────────
function FeatureCard({ icon, title, desc, accent, delay }) {
  const [vis, setVis] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); }}, { threshold: 0.15 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} style={{
      background: "#08111e", border: `1px solid ${accent}18`,
      borderRadius: 12, padding: "24px 22px",
      transition: `opacity .5s ${delay}ms, transform .5s ${delay}ms`,
      opacity: vis ? 1 : 0, transform: vis ? "none" : "translateY(20px)",
    }}
      onMouseOver={e => e.currentTarget.style.borderColor = accent + "50"}
      onMouseOut={e  => e.currentTarget.style.borderColor = accent + "18"}
    >
      <div style={{ fontSize: 28, marginBottom: 14, lineHeight: 1 }}>{icon}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: "#dde6f0", marginBottom: 8, fontFamily: "'Syne',sans-serif" }}>{title}</div>
      <div style={{ fontSize: 13, color: "#4a5e70", lineHeight: 1.75 }}>{desc}</div>
    </div>
  );
}

// ─── Pricing card ─────────────────────────────────────────────────────────────
function PlanCard({ plan, annual }) {
  const price  = annual && plan.annualPrice ? plan.annualPrice : plan.price;
  const period = annual && plan.annualPrice ? "year" : plan.period;
  return (
    <div style={{
      flex: "1 1 280px", maxWidth: 340,
      background: plan.highlight ? "linear-gradient(135deg,#0c1c38 0%,#091528 100%)" : "#08111e",
      border: `1px solid ${plan.highlight ? plan.color + "60" : "#131924"}`,
      borderRadius: 14, padding: "30px 26px",
      position: "relative", display: "flex", flexDirection: "column",
      boxShadow: plan.highlight ? `0 0 40px ${plan.color}18` : "none",
      transform: plan.highlight ? "scale(1.03)" : "none",
    }}>
      {plan.badge && (
        <div style={{ position:"absolute", top:-13, left:"50%", transform:"translateX(-50%)", background:`linear-gradient(90deg,${plan.color},${plan.accent})`, color:"#fff", fontSize:9, fontWeight:800, letterSpacing:1.5, padding:"3px 14px", borderRadius:20 }}>
          {plan.badge}
        </div>
      )}
      <div style={{ fontSize:10, fontWeight:800, color:plan.accent, letterSpacing:2.5, marginBottom:8, fontFamily:"'JetBrains Mono',monospace" }}>{plan.name.toUpperCase()}</div>
      <div style={{ display:"flex", alignItems:"baseline", gap:6, marginBottom:4 }}>
        <span style={{ fontSize: price===0?34:38, fontWeight:800, color:"#dde6f0", fontFamily:"'Syne',sans-serif" }}>{price===0?"Free":inr(price)}</span>
        {price>0 && <span style={{ fontSize:12, color:"#2a3a4a" }}>/ {period}</span>}
      </div>
      {annual && plan.price>0 && plan.annualPrice && (
        <div style={{ fontSize:10, color:"#4ade80", marginBottom:20 }}>Save {inr(plan.price*12 - plan.annualPrice)} vs monthly</div>
      )}
      <div style={{ marginBottom:20, marginTop: (annual && plan.price>0 && plan.annualPrice) ? 0 : 20 }}/>
      <ul style={{ listStyle:"none", padding:0, margin:"0 0 28px", flex:1 }}>
        {plan.features.map((f,i) => (
          <li key={i} style={{ display:"flex", gap:9, alignItems:"flex-start", fontSize:13, color:"#6b7e94", marginBottom:10, lineHeight:1.5 }}>
            <span style={{ color:plan.accent, flexShrink:0, marginTop:2, fontSize:11 }}>✦</span>{f}
          </li>
        ))}
      </ul>
      <a href={plan.ctaLink} style={{
        display:"block", textAlign:"center", padding:"12px",
        background: plan.highlight ? `linear-gradient(90deg,${plan.color},${plan.accent})` : "transparent",
        border:`1px solid ${plan.highlight ? "transparent" : "#1e2a36"}`,
        color: plan.highlight ? "#fff" : plan.accent,
        borderRadius:8, fontSize:13, fontWeight:700, textDecoration:"none",
        letterSpacing:0.5,
      }}>
        {plan.cta}
      </a>
    </div>
  );
}

// ─── FAQ ──────────────────────────────────────────────────────────────────────
function FAQ({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom:"1px solid #0d1a28" }}>
      <button onClick={() => setOpen(o=>!o)} style={{ width:"100%", background:"transparent", border:"none", cursor:"pointer", padding:"20px 0", display:"flex", justifyContent:"space-between", alignItems:"center", gap:16 }}>
        <span style={{ fontSize:15, color:open?"#dde6f0":"#6b7e94", textAlign:"left", fontWeight:open?600:400, fontFamily:"'Syne',sans-serif", transition:"color .2s" }}>{q}</span>
        <span style={{ color:open?"#3b82f6":"#2a3a4a", fontSize:20, flexShrink:0, transition:"transform .2s", transform:open?"rotate(45deg)":"none" }}>+</span>
      </button>
      <div style={{ overflow:"hidden", maxHeight:open?400:0, transition:"max-height .35s ease", paddingBottom:open?20:0 }}>
        <p style={{ fontSize:14, color:"#4a5e70", lineHeight:1.85, margin:0 }}>{a}</p>
      </div>
    </div>
  );
}

const FEATURES = [
  { icon:"▬", title:"Smart Wall Drawing",       desc:"Chain walls with multi-mode snapping — node, edge, midpoint, intersection. Walls auto-split at crossings.", accent:"#3b82f6" },
  { icon:"⚕", title:"Auto-Heal Engine",          desc:"DCEL graph healer merges duplicate nodes, dissolves collinear junctions, removes micro-edges after every action.", accent:"#f97316" },
  { icon:"₹", title:"Live BOQ & Material Costs", desc:"Real-time bill of quantities. Per-wall volume, bricks, cement, sand takeoff. Per-room floor and finish costs.", accent:"#e8a838" },
  { icon:"🚪", title:"Doors & Windows",           desc:"Click any wall to place doors or windows. Openings auto-deduct void volume from masonry calculations.", accent:"#22d3ee" },
  { icon:"📄", title:"PDF / Print Export",        desc:"One-click print layout: room schedule, BOQ table, cost summary — formatted for A4, ready for clients.", accent:"#4ade80" },
  { icon:"☁",  title:"Cloud Sync (Pro/Team)",     desc:"Projects sync to your account in real time. Access from any device. 20-version snapshot history per project.", accent:"#a78bfa" },
  { icon:"↩",  title:"60-Step Undo / Redo",       desc:"Every action is reversible. Drag-node moves are grouped into single undo steps for clean history.", accent:"#fb7185" },
  { icon:"🏷",  title:"Room Labelling",            desc:"Assign room types with per-type floor and wall-finish rates. Area and cost auto-calculated and live in the inspector.", accent:"#34d399" },
];

const STATS = [
  { label:"Materials supported",   val:6,    suffix:"",      prefix:"" },
  { label:"Snap point types",      val:4,    suffix:"",      prefix:"" },
  { label:"Undo history depth",    val:60,   suffix:" steps",prefix:"" },
  { label:"Snapshot history",      val:20,   suffix:" saves",prefix:"" },
];

export default function LandingPage() {
  const [annual, setAnnual] = useState(false);
  const [heroVis, setHeroVis] = useState(false);
  useEffect(() => { const t = setTimeout(() => setHeroVis(true), 80); return () => clearTimeout(t); }, []);

  return (
    <MarketingLayout>
      {/* ── Hero ── */}
      <section style={{ position:"relative", overflow:"hidden", textAlign:"center", padding:"110px 24px 90px", background:"linear-gradient(180deg,#060d1a 0%,#07090f 100%)" }}>
        <BlueprintBg opacity={0.035} />
        {/* Glow orbs */}
        <div style={{ position:"absolute", top:"-10%", left:"20%", width:500, height:500, borderRadius:"50%", background:"radial-gradient(circle,#1e3d6622 0%,transparent 70%)", pointerEvents:"none" }}/>
        <div style={{ position:"absolute", top:"10%",  right:"15%", width:400, height:400, borderRadius:"50%", background:"radial-gradient(circle,#e8a83810 0%,transparent 70%)", pointerEvents:"none" }}/>

        <div style={{ maxWidth:780, margin:"0 auto", position:"relative" }}>
          <div style={{ display:"inline-flex", alignItems:"center", gap:8, fontSize:10, color:"#3b82f6", letterSpacing:2, fontWeight:800, background:"#060f1e", border:"1px solid #1e3d6660", borderRadius:24, padding:"5px 16px", marginBottom:36, fontFamily:"'JetBrains Mono',monospace",
            opacity:heroVis?1:0, transition:"opacity .6s, transform .6s", transform:heroVis?"none":"translateY(-10px)" }}>
            <span style={{ width:6, height:6, borderRadius:"50%", background:"#4ade80", boxShadow:"0 0 8px #4ade80" }}/>
            BROWSER-BASED · NO INSTALL · FREE TO START
          </div>

          <h1 style={{ fontSize:"clamp(36px,6.5vw,68px)", fontWeight:800, color:"#dde6f0", margin:"0 0 22px", lineHeight:1.08, letterSpacing:-2, fontFamily:"'Syne',sans-serif",
            opacity:heroVis?1:0, transition:"opacity .7s .1s, transform .7s .1s", transform:heroVis?"none":"translateY(24px)" }}>
            Draw floor plans.<br/>
            <span style={{ background:"linear-gradient(90deg,#e8a838,#f97316)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>Know the cost instantly.</span>
          </h1>

          <p style={{ fontSize:"clamp(15px,2vw,18px)", color:"#4a5e70", maxWidth:540, margin:"0 auto 44px", lineHeight:1.75,
            opacity:heroVis?1:0, transition:"opacity .7s .2s", }}>
            J.A.D.E is a Joint Aided Design Engine — a professional architectural design tool with live bill of quantities, material takeoffs, and cost estimation — built for architects, contractors and homeowners across India.
          </p>

          <div style={{ display:"flex", gap:14, justifyContent:"center", flexWrap:"wrap",
            opacity:heroVis?1:0, transition:"opacity .7s .35s", }}>
            <a href="/app" style={{ padding:"15px 36px", background:"linear-gradient(90deg,#e8a838,#f97316)", color:"#07090f", borderRadius:8, fontSize:15, fontWeight:800, textDecoration:"none", boxShadow:"0 8px 32px #e8a83830", letterSpacing:0.3 }}>
              Start drawing — it's free →
            </a>
            <a href="#features" style={{ padding:"15px 26px", background:"transparent", border:"1px solid #1e2a40", color:"#6b7e94", borderRadius:8, fontSize:15, textDecoration:"none" }}>
              Explore features
            </a>
          </div>
          <div style={{ marginTop:18, fontSize:11, color:"#1e2a36", opacity:heroVis?1:0, transition:"opacity .7s .5s" }}>
            No sign-up required · Works in any modern browser · Data stays in your browser
          </div>
        </div>

        {/* Fake app preview bar */}
        <div style={{ maxWidth:820, margin:"72px auto 0", position:"relative",
          opacity:heroVis?1:0, transition:"opacity .9s .4s, transform .9s .4s", transform:heroVis?"none":"translateY(32px)" }}>
          <div style={{ background:"#09090f", border:"1px solid #131924", borderRadius:12, overflow:"hidden", boxShadow:"0 32px 80px #000d" }}>
            <div style={{ height:36, background:"#060a10", borderBottom:"1px solid #0d1520", display:"flex", alignItems:"center", padding:"0 16px", gap:8 }}>
              <div style={{ width:10, height:10, borderRadius:"50%", background:"#1e2a36" }}/>
              <div style={{ width:10, height:10, borderRadius:"50%", background:"#1e2a36" }}/>
              <div style={{ width:10, height:10, borderRadius:"50%", background:"#1e2a36" }}/>
              <div style={{ flex:1, textAlign:"center", fontSize:9, color:"#1e2a36", fontFamily:"'JetBrains Mono',monospace" }}>J.A.D.E — Untitled Project</div>
              <div style={{ fontSize:8, color:"#4ade80", fontFamily:"'JetBrains Mono',monospace" }}>● Saved</div>
            </div>
            <div style={{ height:280, background:"#07090f", position:"relative", overflow:"hidden" }}>
              <BlueprintBg opacity={0.04}/>
              {/* Fake walls SVG */}
              <svg style={{ position:"absolute", inset:0, width:"100%", height:"100%" }}>
                <g opacity="0.9">
                  {/* outer walls */}
                  <rect x="160" y="60"  width="500" height="180" fill="none" stroke="#3b82f640" strokeWidth="8" rx="1"/>
                  {/* inner partition */}
                  <line x1="360" y1="60"  x2="360" y2="240" stroke="#3b82f640" strokeWidth="6"/>
                  <line x1="160" y1="150" x2="360" y2="150" stroke="#3b82f640" strokeWidth="6"/>
                  {/* door arc */}
                  <path d="M360 210 Q385 210 385 235" fill="none" stroke="#e8a83880" strokeWidth="2" strokeDasharray="4 3"/>
                  {/* window */}
                  <line x1="450" y1="60" x2="540" y2="60" stroke="#88ccff90" strokeWidth="6"/>
                  {/* dimension label */}
                  <rect x="362" y="140" width="56" height="14" rx="2" fill="#0c1320" stroke="#2a3a50" strokeWidth="0.8"/>
                  <text x="390" y="150" textAnchor="middle" fontSize="9" fill="#60a5fa" fontFamily="monospace">5.00m</text>
                  {/* room labels */}
                  <text x="262" y="108" textAnchor="middle" fontSize="11" fill="#60a5fa99" fontFamily="monospace">🛏 Bedroom</text>
                  <text x="262" y="122" textAnchor="middle" fontSize="9"  fill="#60a5fa66" fontFamily="monospace">12.0 m²</text>
                  <text x="510" y="148" textAnchor="middle" fontSize="11" fill="#fb923c99" fontFamily="monospace">🍳 Kitchen</text>
                  <text x="510" y="162" textAnchor="middle" fontSize="9"  fill="#fb923c66" fontFamily="monospace">9.4 m²</text>
                  <text x="460" y="210" textAnchor="middle" fontSize="11" fill="#22d3ee99" fontFamily="monospace">🚿 Bath</text>
                  <text x="460" y="224" textAnchor="middle" fontSize="9"  fill="#22d3ee66" fontFamily="monospace">4.2 m²</text>
                  {/* nodes */}
                  {[[160,60],[660,60],[660,240],[160,240],[360,60],[360,150],[360,240],[160,150]].map(([x,y],i) => (
                    <circle key={i} cx={x} cy={y} r={4} fill="#07090f" stroke="#3b82f6" strokeWidth="1.5"/>
                  ))}
                </g>
              </svg>
              {/* Inspector panel hint */}
              <div style={{ position:"absolute", right:0, top:0, bottom:0, width:110, background:"#09090fcc", borderLeft:"1px solid #131924", padding:"10px 8px", fontSize:8, color:"#3d5068", fontFamily:"'JetBrains Mono',monospace" }}>
                <div style={{ color:"#e8a838", fontWeight:700, marginBottom:8 }}>TOTAL</div>
                <div style={{ fontSize:14, color:"#e8a838", fontWeight:800, marginBottom:8 }}>₹4,28,500</div>
                <div style={{ color:"#3b82f6", marginBottom:3 }}>Structure</div>
                <div style={{ color:"#60a5fa", marginBottom:8 }}>₹1,84,200</div>
                <div style={{ color:"#4ade80", marginBottom:3 }}>Finishes</div>
                <div style={{ color:"#86efac" }}>₹2,44,300</div>
              </div>
            </div>
          </div>
          <div style={{ position:"absolute", inset:0, borderRadius:12, boxShadow:"inset 0 -60px 40px #07090f", pointerEvents:"none" }}/>
        </div>
      </section>

      {/* ── Stats ── */}
      <section style={{ background:"#060a10", borderTop:"1px solid #0d1520", borderBottom:"1px solid #0d1520" }}>
        <div style={{ maxWidth:900, margin:"0 auto", padding:"40px 24px", display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))", gap:0 }}>
          {STATS.map((s,i) => (
            <div key={i} style={{ textAlign:"center", padding:"20px", borderRight: i<STATS.length-1?"1px solid #0d1520":"none" }}>
              <div style={{ fontSize:36, fontWeight:800, color:"#dde6f0", fontFamily:"'Syne',sans-serif", marginBottom:4 }}>
                <Counter to={s.val} suffix={s.suffix} prefix={s.prefix}/>
              </div>
              <div style={{ fontSize:11, color:"#2a3a4a", letterSpacing:1, textTransform:"uppercase" }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Features ── */}
      <section id="features" style={{ maxWidth:1100, margin:"0 auto", padding:"96px 24px" }}>
        <div style={{ textAlign:"center", marginBottom:64 }}>
          <div style={{ fontSize:10, color:"#3b82f6", letterSpacing:3, fontWeight:800, marginBottom:12, fontFamily:"'JetBrains Mono',monospace" }}>FEATURES</div>
          <h2 style={{ fontSize:"clamp(24px,4vw,42px)", fontWeight:800, color:"#dde6f0", margin:"0 0 16px", fontFamily:"'Syne',sans-serif", letterSpacing:-1 }}>Everything to plan and price a build</h2>
          <p style={{ fontSize:15, color:"#4a5e70", maxWidth:500, margin:"0 auto", lineHeight:1.7 }}>From rough sketch to detailed BOQ — J.A.D.E handles the geometry and the arithmetic.</p>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))", gap:16 }}>
          {FEATURES.map((f,i) => <FeatureCard key={f.title} {...f} delay={i*60}/>)}
        </div>
      </section>

      {/* ── Pricing ── */}
      <section id="pricing" style={{ background:"#060a10", position:"relative", overflow:"hidden" }}>
        <BlueprintBg opacity={0.02}/>
        <div style={{ maxWidth:1100, margin:"0 auto", padding:"96px 24px", position:"relative" }}>
          <div style={{ textAlign:"center", marginBottom:56 }}>
            <div style={{ fontSize:10, color:"#3b82f6", letterSpacing:3, fontWeight:800, marginBottom:12, fontFamily:"'JetBrains Mono',monospace" }}>PRICING</div>
            <h2 style={{ fontSize:"clamp(24px,4vw,42px)", fontWeight:800, color:"#dde6f0", margin:"0 0 16px", fontFamily:"'Syne',sans-serif", letterSpacing:-1 }}>Simple, honest pricing</h2>
            <p style={{ fontSize:15, color:"#4a5e70", maxWidth:460, margin:"0 auto", lineHeight:1.7 }}>Start free. Upgrade when your projects need cloud sync, snapshot history, or team collaboration.</p>
          </div>
          {/* Toggle */}
          <div style={{ display:"flex", justifyContent:"center", alignItems:"center", gap:12, marginBottom:48 }}>
            <span style={{ fontSize:13, color: annual?"#2a3a4a":"#8496a8" }}>Monthly</span>
            <button onClick={() => setAnnual(a=>!a)} style={{ width:46, height:26, borderRadius:13, border:"none", cursor:"pointer", background:annual?"#3b82f6":"#131924", position:"relative", transition:"background .2s" }}>
              <span style={{ position:"absolute", top:3, left:annual?22:3, width:20, height:20, background:"#fff", borderRadius:"50%", transition:"left .2s", boxShadow:"0 1px 4px #0006" }}/>
            </button>
            <span style={{ fontSize:13, color:annual?"#8496a8":"#2a3a4a" }}>Annual <span style={{ color:"#4ade80", fontSize:11, fontWeight:700 }}>–25%</span></span>
          </div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:20, justifyContent:"center", alignItems:"flex-start" }}>
            {PLANS.map(p => <PlanCard key={p.id} plan={p} annual={annual}/>)}
          </div>
          <p style={{ textAlign:"center", marginTop:32, fontSize:11, color:"#1e2a3a", lineHeight:1.8 }}>{BILLING_NOTE}</p>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" style={{ maxWidth:760, margin:"0 auto", padding:"96px 24px" }}>
        <div style={{ textAlign:"center", marginBottom:56 }}>
          <div style={{ fontSize:10, color:"#3b82f6", letterSpacing:3, fontWeight:800, marginBottom:12, fontFamily:"'JetBrains Mono',monospace" }}>FAQ</div>
          <h2 style={{ fontSize:"clamp(22px,4vw,38px)", fontWeight:800, color:"#dde6f0", fontFamily:"'Syne',sans-serif", letterSpacing:-1 }}>Common questions</h2>
        </div>
        {FAQS.map((f,i) => <FAQ key={i} q={f.q} a={f.a}/>)}
      </section>

      {/* ── CTA ── */}
      <section style={{ textAlign:"center", padding:"96px 24px", position:"relative", overflow:"hidden", background:"linear-gradient(180deg,#07090f 0%,#060d1a 100%)" }}>
        <div style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", width:600, height:300, borderRadius:"50%", background:"radial-gradient(ellipse,#e8a83812 0%,transparent 70%)", pointerEvents:"none" }}/>
        <div style={{ position:"relative" }}>
          <h2 style={{ fontSize:"clamp(24px,4vw,44px)", fontWeight:800, color:"#dde6f0", marginBottom:16, fontFamily:"'Syne',sans-serif", letterSpacing:-1 }}>
            Ready to plan your next build?
          </h2>
          <p style={{ fontSize:15, color:"#4a5e70", marginBottom:40, maxWidth:400, margin:"0 auto 40px" }}>
            Free forever. No credit card. No install.<br/>Your data stays in your browser on the free plan.
          </p>
          <a href="/app" style={{ display:"inline-block", padding:"16px 48px", background:"linear-gradient(90deg,#e8a838,#f97316)", color:"#07090f", borderRadius:8, fontSize:16, fontWeight:800, textDecoration:"none", boxShadow:"0 8px 40px #e8a83838", letterSpacing:0.5 }}>
            Open J.A.D.E free →
          </a>
          <div style={{ marginTop:22, fontSize:12, color:"#1e2a36" }}>
            Questions? <a href={`mailto:${BRAND.email}`} style={{ color:"#3b82f6", textDecoration:"none" }}>{BRAND.email}</a>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
